import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '@/components/Navbar';
import SiteFooter from '@/components/SiteFooter';
import ListingCard from '@/components/ListingCard';
import api from '@/api/client';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, X, ArrowLeft, ArrowRight } from 'lucide-react';

const LIMIT = 12;

export default function BrowseListings() {
  const [listings, setListings] = useState([]);
  const [featuredListings, setFeaturedListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [listingTypes, setListingTypes] = useState([]);
  const [filters, setFilters] = useState({ search: '', category_id: '', type_id: '' });
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
    api.get('/listing-types').then(r => setListingTypes(r.data)).catch(() => {});
  }, []);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', LIMIT);
      if (filters.search) params.append('search', filters.search);
      if (filters.category_id && filters.category_id !== 'all') params.append('category_id', filters.category_id);
      if (filters.type_id && filters.type_id !== 'all') params.append('type_id', filters.type_id);
      const r = await api.get(`/listings?${params}`);
      const all = r.data.listings || [];
      const featured = all.filter(l => l.featured);
      const regular = all.filter(l => !l.featured);
      setFeaturedListings(page === 1 ? featured : []);
      setListings(regular);
      setTotal(r.data.total || 0);
    } catch {
      setListings([]);
      setFeaturedListings([]);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  const clearFilters = () => { setFilters({ search: '', category_id: '', type_id: '' }); setPage(1); };
  const hasFilters = filters.search || filters.category_id || filters.type_id;
  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div style={{ backgroundColor: '#fff', minHeight: '100vh', color: '#000' }}>
      <Navbar />

      {/* Page header — dark band */}
      <section style={{ backgroundColor: '#0A0A0A', color: '#fff' }}>
        <div className="container-app py-16 md:py-20">
          <p className="text-xs uppercase tracking-[0.3em] mb-4 text-white/55">The Catalogue</p>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <h1 className="font-bold" style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)', lineHeight: 1, letterSpacing: '-0.03em' }}>
              Browse Listings
            </h1>
            {!loading && (
              <p className="text-sm text-white/50 tabular-nums">
                <span className="text-white font-bold">{total}</span> work{total !== 1 ? 's' : ''} available
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Filter strip */}
      <div style={{ borderBottom: '1px solid #E5E5E5', backgroundColor: '#FAFAFA' }}>
        <div className="container-app py-4">
          <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
            <div className="relative flex-1" style={{ minWidth: '240px' }}>
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#999' }} />
              <input
                placeholder="Search artworks, artists..."
                value={filters.search}
                onChange={e => { setFilters(f => ({ ...f, search: e.target.value })); setPage(1); }}
                className="w-full pl-9 pr-3 py-2.5 text-sm focus:outline-none"
                style={{ border: '1px solid #E5E5E5', backgroundColor: '#fff', color: '#000' }}
                data-testid="browse-search-input"
              />
            </div>
            <Select value={filters.category_id || 'all'} onValueChange={v => { setFilters(f => ({ ...f, category_id: v === 'all' ? '' : v })); setPage(1); }}>
              <SelectTrigger className="w-full md:w-44 text-sm h-9" style={{ border: '1px solid #E5E5E5', borderRadius: '0' }} data-testid="browse-category-select">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.type_id || 'all'} onValueChange={v => { setFilters(f => ({ ...f, type_id: v === 'all' ? '' : v })); setPage(1); }}>
              <SelectTrigger className="w-full md:w-44 text-sm h-9" style={{ border: '1px solid #E5E5E5', borderRadius: '0' }} data-testid="browse-type-select">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {listingTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {hasFilters && (
              <button onClick={clearFilters}
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider transition-opacity hover:opacity-50"
                style={{ color: '#666' }}>
                <X size={12} /> Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container-app py-14">

        {/* Featured pinned section */}
        {!loading && featuredListings.length > 0 && (
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-8" style={{ borderBottom: '1px solid #000', paddingBottom: '0.875rem' }}>
              <span className="text-[11px] uppercase tracking-[0.25em] font-semibold">Featured Works</span>
              <span className="text-[11px] tabular-nums" style={{ color: '#CCC' }}>{featuredListings.length}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredListings.map(l => <ListingCard key={l.id} listing={l} featured />)}
            </div>
            {listings.length > 0 && (
              <div className="mt-14 pt-8" style={{ borderTop: '1px solid #E5E5E5' }}>
                <span className="text-[11px] uppercase tracking-[0.25em] font-semibold" style={{ color: '#999' }}>All Works</span>
              </div>
            )}
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i}>
                <div style={{ height: '360px', backgroundColor: '#F5F5F5', animation: 'pulse 1.5s ease-in-out infinite' }} />
                <div className="pt-4 space-y-2">
                  <div style={{ height: '11px', backgroundColor: '#F0F0F0', width: '55%' }} />
                  <div style={{ height: '15px', backgroundColor: '#F0F0F0', width: '75%' }} />
                </div>
              </div>
            ))}
          </div>
        ) : listings.length === 0 && featuredListings.length === 0 ? (
          <div className="text-center py-24" style={{ border: '1px solid #E5E5E5' }}>
            <p className="text-2xl font-bold mb-2">No works found</p>
            <p className="text-sm mb-7" style={{ color: '#666' }}>
              {hasFilters ? 'Try adjusting your search or filters' : 'The catalogue is being curated — check back soon'}
            </p>
            {hasFilters && (
              <button onClick={clearFilters}
                className="px-8 py-3 text-sm font-semibold uppercase tracking-wider transition-colors"
                style={{ border: '1px solid #000', backgroundColor: '#000', color: '#fff' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#000'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#000'; e.currentTarget.style.color = '#fff'; }}>
                Clear all filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="browse-listings-grid">
            {listings.map(l => <ListingCard key={l.id} listing={l} />)}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-14 pt-6" style={{ borderTop: '1px solid #E5E5E5' }} data-testid="browse-pagination">
            <button onClick={() => setPage(p => p - 1)} disabled={page === 1}
              className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider disabled:opacity-25 transition-opacity hover:opacity-60"
              style={{ color: '#000' }}>
              <ArrowLeft size={14} /> Previous
            </button>
            <span className="text-xs uppercase tracking-[0.2em]" style={{ color: '#999' }}>
              Page {page} of {totalPages}
            </span>
            <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}
              className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider disabled:opacity-25 transition-opacity hover:opacity-60"
              style={{ color: '#000' }}>
              Next <ArrowRight size={14} />
            </button>
          </div>
        )}
      </div>

      <SiteFooter />
    </div>
  );
}
